<script>
  import { getContext } from "svelte";
  import { tooltip, Modal } from "svelte-utilities";
  import { DimText } from "dimtext";
  import { midpoint } from "vector";
  import { createItem, createLocation, hydrateItem, createGridLines } from "@local/lamina-core";
  import cloneDeep from "lodash/cloneDeep";
  import set from "lodash/set";
  import SidebarSection from "./SidebarSection.svelte";
  import TextInput from "./TextInput.svelte";
  import SelectInput from "./SelectInput.svelte";
  import BooleanInput from "./BooleanInput.svelte";
  import ColorInput from "#lib/sidebar/ColorInput.svelte";
  import CopyTextField from "./CopyTextField.svelte";
  import { scaleOptions } from "@local/extensions/utilities/scales.js";
  import { lengthDim, rectangleDims } from "#src/extensions/dimension-from-points.js";
  import { user } from "#src/stores/auth.js";
  import { nextMark } from "@local/extensions/identifiers/mark.js";
  import {
    selectedLocations,
    selectedDimensions,
    documentViewCollectionFilter,
    documentViewItemFilter,
    documentViewLocationFilter,
  } from "#src/stores/ui.js";
  import makeMultiCategory from "#src/extensions/multi-category.js";
  import nameMinusExtension from "@local/extensions/utilities/name-minus-extension.js";
  import extension from "@local/extensions/utilities/extension.js";
  import { multiplier } from "@local/extensions/utilities/scales.js";
  import { UX_MODE } from "#src/env.js";

  export let dimensions;
  export let group;
  export let items;
  export let collections;
  export let types;
  export let disabled = false;
  export let doc;
  export let locations;

  const dt = new DimText();
  const fileData = getContext("fileData");

  let showAdvancedSettings = true;
  let convertModal;
  let importAs = "collection";
  let deleteDims = true;
  let importedDimensionIs = "width";

  $: dim = dimensions.length === 1 ? dimensions[0] : makeMultiCategory(dimensions);
  $: precisionValue = getPrecisionValue(dim);
  $: precisionOptions = getPrecisionOptions(dim);
  $: alignedDims = dimensions.filter((d) => d.type === "aligned");
  $: rectDims = dimensions.filter((d) => d.type === "rectangular");

  function getPrecisionProp(dim) {
    if (!dim) return null;

    if (dim.display_unit === "inches") {
      if (dim.format === "DECIMAL") {
        return "decimal_precision";
      } else {
        return "fractional_precision";
      }
    } else {
      return "mm_precision";
    }
  }

  function getPrecisionValue(dim) {
    const prop = getPrecisionProp(dim);
    if (!prop) return null;
    return dim[prop];
  }

  function getPrecisionOptions(dim) {
    if (!dim) return [];

    if (dim.display_unit === "inches") {
      if (dim.format === "DECIMAL") {
        return [
          { label: "0", value: 0 },
          { label: "1", value: 1 },
          { label: "2", value: 2 },
          { label: "3", value: 3 },
          { label: "4", value: 4 },
        ];
      } else {
        return [
          { label: '1"', value: 0 },
          { label: '1/2"', value: 1 },
          { label: '1/4"', value: 2 },
          { label: '1/8"', value: 3 },
          { label: '1/16"', value: 4 },
          { label: '1/32"', value: 5 },
        ];
      }
    } else {
      return [
        { label: "0", value: 0 },
        { label: "1", value: 1 },
        { label: "2", value: 2 },
      ];
    }
  }

  async function updateDim(prop, value) {
    if (Array.isArray(dim.id)) {
      const updates = dim.id.map((id) => ({
        type: "document_dimension",
        id,
        diff: { [prop]: value },
      }));
      group.update(updates);
    } else {
      group.update([
        {
          type: "document_dimension",
          id: dim.id,
          diff: { [prop]: value },
        },
      ]);
    }
  }

  function displaySettings(dim) {
    const displayUnit = dim.display_unit;
    const dimFormat = displayUnit === "inches" ? dim.format : "DECIMAL";
    const sddp = dim.decimal_precision;
    const sdfp = dim.fractional_precision;
    const dimPrecision =
      displayUnit === "inches" ? (dimFormat === "DECIMAL" ? sddp : sdfp) : dim.mm_precision;

    return { displayUnit, dimFormat, dimPrecision };
  }

  function updateDimValues(dim) {
    const ds = displaySettings(dim);

    if (dim.type === "aligned") {
      const length = lengthDim(dim.data.points[0], dim.data.points[1], dim.scale, ds);
      if (length == null) return null;
      dim.data.values.length = length;
    } else if (dim.type === "rectangular") {
      const rdims = rectangleDims(dim.data.points[0], dim.data.points[1], dim.scale, ds);
      if (!rdims) return null;
      const [width, height, area] = rdims;
      dim.data.values.width = width;
      dim.data.values.height = height;
      dim.data.values.area = area;
    }

    return dim.data;
  }

  function updateDimProp(e, prop) {
    const updates = dimensions.map((dim) => {
      const updatedDim = cloneDeep(dim);
      updatedDim[prop] = e.detail.value;
      const diff = { data: updateDimValues(updatedDim), [prop]: e.detail.value };

      return { type: "document_dimension", id: dim.id, diff };
    });

    group.update(updates);
  }

  function updatePrecision(e) {
    const prop = getPrecisionProp(dim);
    if (!prop) return;

    const updates = dimensions.map((dim) => {
      const updatedDim = cloneDeep(dim);
      updatedDim[prop] = e.detail.value;
      const diff = { data: updateDimValues(updatedDim), [prop]: e.detail.value };
      return { type: "document_dimension", id: dim.id, diff };
    });

    group.update(updates);
  }

  function numParser(v) {
    const result = parseFloat(v);
    if (isNaN(result)) throw new Error("Not parseable as a number");
    if (result <= 0) throw new Error("Value must be positive");
    return result;
  }

  function drawingFromClip(loc) {
    const page = doc.data.pages[loc.page_id];
    const path = `${nameMinusExtension(page.name)}.${page.page}.tiff`;
    const ext = extension(page.name);
    const file = $fileData[page.name];

    return {
      attachment_id: loc.id,
      id: page.name.split("/")[0],
      name: page.label || file.file.user_metadata?.name,
      type: "iiif",
      clip: loc.clip,
      path,
      extension: ext,
      properties: {
        width: loc.clip.width,
        height: loc.clip.height,
      },
    };
  }

  function convertToShapes() {
    const records = importAs === "item" ? items : collections;
    const newShapes = dimensions.map((dim) => {
      const item = createItem($group.id, $user.id);
      item.is_collection = importAs === "collection";
      if (dim.type === "aligned") {
        if (importedDimensionIs === "width") {
          item.width = dt.parse(dim.data.values.length).value;
        } else {
          item.height = dt.parse(dim.data.values.length).value;
        }
      } else if (dim.type === "rectangular") {
        item.width = dt.parse(dim.data.values.width).value;
        item.height = dt.parse(dim.data.values.height).value;
      }

      if (importAs === "collection" && $documentViewCollectionFilter) {
        if ($documentViewCollectionFilter.operator === "is_equal_to") {
          set(item, $documentViewCollectionFilter.field, $documentViewCollectionFilter.value);
        }
      } else if ($documentViewItemFilter) {
        if ($documentViewItemFilter.operator === "is_equal_to") {
          set(item, $documentViewItemFilter.field, $documentViewItemFilter.value);
        }
      }

      const hydrated = hydrateItem(item, $group);

      if (hydrated.is_collection) {
        hydrated.x_subdivisions = 1;
        hydrated.y_subdivisions = 1;
        const gl = createGridLines(hydrated.cache.width, hydrated.cache.height, 1, 1);
        hydrated.data.reference_planes = gl;
      }

      return hydrated;
    });
    newShapes.forEach((shape, i) => {
      const mks = newShapes.slice(0, i);
      shape.mark = nextMark([...records, ...mks]);
      shape.show_drawing_overlay = true;
    });
    const locs = dimensions.map((dim, idx) => {
      const item = newShapes[idx];
      const position = midpoint(dim.data.points[0], dim.data.points[1]);

      const location = createLocation(
        {
          group_id: $group.id,
          index: locations.length + idx,
          position,
          document_id: dim.document_id,
          page_id: dim.page_id,
          record_type: importAs === "item" ? "item" : "collection",
          record_id: item.id,
        },
        $documentViewLocationFilter,
      );

      if (dim.type === "rectangular") {
        const page = doc.data.pages[dim.page_id];
        const minX = Math.min(dim.data.points[0].x, dim.data.points[1].x);
        const minY = Math.min(dim.data.points[0].y, dim.data.points[1].y);
        const maxX = Math.max(dim.data.points[0].x, dim.data.points[1].x);
        const maxY = Math.max(dim.data.points[0].y, dim.data.points[1].y);

        const drawing_offset = { x: 0, y: 0 };
        if (page.scale) {
          const m = multiplier(page.scale);
          const offset = (25 * m) / 300;
          drawing_offset.x = -offset;
          drawing_offset.y = -offset;
        }

        location.clip = {
          x: minX - 25,
          y: minY - 25,
          width: Math.max(25, maxX + 25 - (minX - 25)),
          height: Math.max(25, maxY + 25 - (minY - 25)),
          drawing_scale: page.scale || null,
          drawing_offset,
        };

        const ext = extension(page.name);
        if (ext === "pdf") {
          const drawing = drawingFromClip(location);
          item.drawing = drawing;
        }
      }

      return location;
    });
    const updates = [
      { type: "item", action: "add", records: newShapes, block: true },
      { type: "location", action: "add", records: locs },
    ];
    if (deleteDims) {
      const deletedIds = dimensions.map((dim) => dim.id);
      updates.push({ type: "document_dimension", action: "remove", ids: deletedIds });
    }
    group.update(updates);
    $selectedDimensions = {};
    selectedLocations.select(...locs.map((l) => l.id));
  }
</script>

<SidebarSection>
  <div class="flex items-center justify-between">
    {#if dimensions.length > 1}
      <h3 class="px-2 mb-2">Dimension ({dimensions.length} selected)</h3>
    {:else}
      <h3 class="px-2 mb-2">Dimension</h3>
    {/if}
  </div>
  {#if dim.type === "aligned" && !Array.isArray(dim.id)}
    <CopyTextField label="Length" labelWidth="7.5rem" value={dim?.data.values.length} />
  {:else if dim.type === "rectangular" && !Array.isArray(dim.id)}
    <CopyTextField label="Width" labelWidth="7.5rem" value={dim?.data.values.width} />
    <CopyTextField label="Height" labelWidth="7.5rem" value={dim?.data.values.height} />
    {#if dim?.data.values.area}
      <CopyTextField label="Area" labelWidth="7.5rem" value={dim?.data.values.area} />
    {/if}
  {:else}
    <TextInput disabled label="Dimensions" labelWidth="7.5rem" value="Multiple Selected" />
  {/if}
  <SelectInput
    {disabled}
    label="Scale"
    labelWidth="7.5rem"
    options={scaleOptions}
    value={dim?.scale}
    on:input={(e) => updateDimProp(e, "scale")} />
  <SelectInput
    {disabled}
    label="Display Unit"
    labelWidth="7.5rem"
    options={[
      { label: "Inches, sf", value: "inches" },
      { label: "Millimeters, m²", value: "millimeters" },
    ]}
    value={dim?.display_unit}
    on:input={(e) => updateDimProp(e, "display_unit")} />
  {#if dim?.display_unit === "inches"}
    <SelectInput
      {disabled}
      label="Format"
      labelWidth="7.5rem"
      options={[
        { label: "Decimal", value: "DECIMAL" },
        { label: "Fractional", value: "FRACTIONAL" },
      ]}
      value={dim?.format}
      on:input={(e) => updateDimProp(e, "format")} />
  {/if}
  <SelectInput
    {disabled}
    label="Precision"
    labelWidth="7.5rem"
    options={precisionOptions}
    value={precisionValue}
    on:input={updatePrecision} />
  {#if showAdvancedSettings}
    <SelectInput
      {disabled}
      label="Scale Reference"
      labelWidth="7.5rem"
      value={dim?.scale_reference}
      options={[
        { label: "Screen Space", value: "screen_space" },
        { label: "Paper Space", value: "paper_space" },
      ]}
      on:input={(e) => updateDim("scale_reference", e.detail.value)} />
    <TextInput
      {disabled}
      label="Text Size"
      labelWidth="7.5rem"
      value={dim?.size}
      postlabel={dim?.scale_reference === "screen_space" ? "px" : "pts"}
      parser={numParser}
      on:input={(e) => updateDim("size", e.detail.value)} />
    <ColorInput
      {disabled}
      label="Color Override"
      labelWidth="7.5rem"
      value={dim?.color_override}
      nullable
      on:input={(e) => updateDim("color_override", e.detail.value)} />
  {/if}
  {#if !disabled}
    <button class="btn w-full my-2" on:click={() => convertModal.open()}> Convert to Shape </button>
  {/if}
</SidebarSection>

<Modal
  bind:this={convertModal}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Convert", type: "confirm", style: "primary" },
  ]}
  on:confirm={convertToShapes}>
  <div slot="title">Convert to Shape</div>
  <div slot="content" class="space-y-4 text-sm">
    {#if rectDims.length && alignedDims.length}
      <p>
        {alignedDims.length} aligned dimension(s) and {rectDims.length} rectangular dimension(s) will be converted
        to shapes.
      </p>
    {:else if rectDims.length}
      <p>{rectDims.length} rectangular dimension(s) will be converted to shapes.</p>
    {:else if alignedDims.length}
      <p>{alignedDims.length} aligned dimension(s) will be converted to shapes.</p>
    {/if}
    <div class="text-xs space-y-2">
      {#if UX_MODE !== "shpx"}
        <SelectInput
          label="Import as"
          labelWidth="8rem"
          border
          options={[
            { label: "Items", value: "item" },
            { label: "Openings", value: "collection" },
          ]}
          bind:value={importAs} />
      {/if}
      {#if alignedDims.length}
        <SelectInput
          label="Imported dimension is"
          labelWidth="8rem"
          border
          options={[
            { label: "Width", value: "width" },
            { label: "Height", value: "height" },
          ]}
          bind:value={importedDimensionIs} />
      {/if}
      <BooleanInput border label="Delete Dimensions" labelWidth="8rem" bind:value={deleteDims} />
    </div>
  </div>
</Modal>
